import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth/services';

import { FleetLprService } from '../../integrations';
import { ViLPRProvider } from '../../shared/models';
import { getBoundingBox } from '../../utils/utils';
import { LPR_ERRORS, LprWarningEntry } from '../constants/errors';
import { PlateResultSort } from '../constants/parameters';
import { IFsHistoricalProviderResultsInterface, LPRResultsFacets } from '../interfaces';
import { IFsLprParameters } from '../interfaces/common/parameters';
import { IFsLprProviderInterface } from '../interfaces/common/provider';
import {
  FleetSortOrder,
  IFsLprFleetParameters,
  RECORD_TYPE_TO_FLEET_RECORD_TYPE,
} from '../interfaces/lpr-fleet-parameters.interface';
import { FsLprFleetResults } from '../interfaces/lpr-fleet-results';
import { FsLprFleetRead } from '../models/lpr-fleet-read';

// Fleet requires searchReason, so Partial<> needs to keep it required
export type PartialFleetParams = Partial<Omit<IFsLprFleetParameters, 'searchReason'>> & {
  searchReason: string;
};

/**
 * @deprecated This service is legacy and will be removed in a future release.
 *
 * DO NOT USE THIS SERVICE IN NEW CODE.
 *
 * This service provides LPR data from the old fleet_ps integration and is only kept for
 * backwards compatibility during the transition period. The fallback to fleet_ps when
 * Axon VI is disabled has been removed. Use AxonVIProviderService instead.
 *
 * Scheduled for removal: TBD
 */
@Injectable({ providedIn: 'root' })
export class FleetProviderService implements IFsLprProviderInterface {
  public name = 'Fleet 3';
  public nameEnum?: ViLPRProvider;

  public get loaded$(): Observable<boolean> {
    return this._loaded$;
  }

  public errors: string[] = [];
  public warnings: LprWarningEntry[] = [];

  public isLoading: boolean;
  public isInit: boolean;
  public isFinished: boolean = false;
  public total: number;

  private _params: IFsLprFleetParameters;
  private _loaded$: Subject<boolean> = new Subject<boolean>();

  private _inited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly _destroy$: Subject<void> = new Subject<void>();
  private readonly _cancel$: Subject<void> = new Subject<void>();

  constructor(
    public readonly _fleetLprService: FleetLprService,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    if (this._sdmEnabled) {
      this.isLoading = true;
      this.isInit = false;
      this.total = 0;
    }
  }

  historicLoaded$: Observable<boolean>;

  public results: FsLprFleetRead[] = [];
  public facets: LPRResultsFacets;

  public init(): void {
    this.isInit = true;
    this._inited$.next(true);
  }

  public search(query: Partial<IFsLprParameters>): void {
    const inited$ = this._sdmEnabled
      ? this._inited$.pipe(
          filter((inited) => inited),
          take(1),
        )
      : this._inited$;
    inited$.subscribe((inited) => {
      if (inited) {
        this._startSearch(query);
      }
    });
  }

  public next(): void {
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    this._params.sortValues = this.results[this.results.length - 1].sortValues.toString();
    this._fleetLprService
      .getFleetPlates(this._params)
      .pipe(
        takeUntil(this._cancel$),
        catchError(() => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_NEXT_PAGE);
          this.isLoading = false;
          this.isFinished = true;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  public getHistoricalResults(
    searchParams: Partial<IFsLprParameters>,
  ): Observable<IFsHistoricalProviderResultsInterface> {
    const modifiedParams = this._historicalSearchTransformParams(searchParams);

    return this._fleetLprService.getFleetPlates(modifiedParams).pipe(
      map((response: FsLprFleetResults) => {
        return {
          results: response.data || [],
          total: response.metadata.totalQueryResults || 0,
        };
      }),
      catchError(() => {
        return of(null);
      }),
      takeUntil(this._destroy$),
    );
  }

  public cancel(): void {
    this._cancel$.next();
  }

  public getReadById(
    readId: string,
    timestamp: number,
    searchReason: string,
  ): Observable<FsLprFleetRead> {
    return this._fleetLprService.getRead({ readId, timestamp, searchReason });
  }

  private get _sdmEnabled(): boolean {
    return this._currentOrganization?.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  private _startSearch(query: Partial<IFsLprParameters>): void {
    this.cancel(); // Cancel any ongoing search
    this.isFinished = false;
    this.errors = [];
    this.warnings = [];
    this.results = [];
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    // Searching should refetch facets
    this.facets = null;

    const params = this._searchTransformParams(query);
    if (this.errors.length > 0) {
      this.isLoading = false;
      this.isFinished = true;
      this.total = 0;
      this._loaded$.next(true);
      return;
    }
    this._fleetLprService
      .getFleetPlates(params)
      .pipe(
        takeUntil(this._cancel$),
        catchError((err) => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_SEARCH_RESULTS);
          this.isLoading = false;
          this.isFinished = true;
          this.total = 0;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  private _handleResponse(response: FsLprFleetResults): void {
    /**
     * Having BE return facets is a costly operation, so we only want it to happen when doing "full searches"
     * The providers can either fetch reads by calling search() or next()
     * - search() is called when doing a new search with new parameters, and should include facets
     * - next() is called when fetching the next page of results for an existing search, and should NOT include facets
     */
    this._params.includeFacets = false;

    if (response) {
      this.facets = this.facets || response.metadata?.facets;

      const newDataCount = response.data?.length || 0;
      this.total = response.metadata?.totalQueryResults ?? this.results.length + newDataCount;
      this.results = [...this.results, ...response.data];
      this.isFinished = this.results.length >= this.total || this.total === 0;
      this.isLoading = false;
      this._loaded$.next(true);
    }
  }

  private _baseTransformParams(params: Partial<IFsLprParameters>): PartialFleetParams {
    const {
      includeFacets,
      searchReason,
      plate,
      fromDate: _fromDate,
      toDate: _toDate,
      limit: _limit,
      plateState,
      vehicleMake,
      vehicleBody,
      vehicleName,
      vehicleColor: _vehicleColor,
      inBbox: _inBbox,
      recordType: _recordType,
      assignedUserIds: _assignedUserIds,
      deviceNames: _deviceNames,
      sort: _sort,
      topRack,
      windowStickers,
      bumperStickers,
    } = params;

    const baseParams: PartialFleetParams = {
      includeFacets,
      cameraVendors: this._sdmEnabled ? params.cameraVendors : undefined,
      cameraModels: this._sdmEnabled ? params.cameraModels : undefined,
      searchReason,
      plate,
      fromDate: _fromDate?.toISOString(),
      toDate: _toDate?.toISOString(),
      limit: _limit?.toString(),
      sort:
        _sort === PlateResultSort.ASC
          ? FleetSortOrder.CAPTURE_TIME_ASC
          : FleetSortOrder.CAPTURE_TIME_DESC,
      recordType: RECORD_TYPE_TO_FLEET_RECORD_TYPE[_recordType],
      vehicleMake,
      vehicleBody,
      vehicleName,
      ...(_inBbox && {
        inBbox: getBoundingBox(
          _inBbox,
          this._sdmEnabled && this.nameEnum ? this.nameEnum : this.name,
        ),
      }),
      ...(plateState && { plateState }),
      ...(topRack && { topRack }),
      ...(windowStickers && { windowStickers }),
      ...(bumperStickers && { bumperStickers }),
    };

    // Fleet API requires vehicle color as a comma separated string, not an array
    if (Array.isArray(_vehicleColor) && _vehicleColor.length) {
      baseParams.vehicleColor = _vehicleColor.join(',');
    }

    if (Array.isArray(_assignedUserIds) && _assignedUserIds.length) {
      baseParams.assignedUserIds = _assignedUserIds.join(',');
    }

    if (Array.isArray(_deviceNames) && _deviceNames.length > 0) {
      baseParams.cameraNames = _deviceNames.join(',');
    }

    return baseParams;
  }

  private _searchTransformParams(params: Partial<IFsLprParameters>): IFsLprFleetParameters {
    this._params = this._baseTransformParams(params);

    return this._params;
  }

  private _historicalSearchTransformParams(
    params: Partial<IFsLprParameters>,
  ): IFsLprFleetParameters {
    return this._baseTransformParams(params);
  }
}
